/**
 * Current-user context. Data is scoped by org (the gateway partitions by tenant);
 * this adds a *viewer* on top — which person in the org you're acting as. Screens
 * read `useCurrentUser()` to resolve the active person and can filter their data
 * by it (e.g. the Inbox shows only items addressed to the current user).
 *
 * The selection is per-org (each org has its own people) and persisted. When an
 * org has no stored selection, it falls back to the person flagged `you` in the
 * directory, else the first person.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { OrgId, Person } from '@wand/shared';
import { useOrg } from './OrgContext.js';
import { usePeople } from '../gateway/hooks.js';

const STORAGE_KEY = 'wand-active-user';
type UserMap = Record<OrgId, string>;

function loadSelection(): UserMap {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as UserMap;
  } catch {
    return {};
  }
}

interface UserSelectionValue {
  selection: UserMap;
  selectUser: (org: OrgId, id: string) => void;
}
const Ctx = createContext<UserSelectionValue | null>(null);

export function UserProvider({ children }: { children: ReactNode }) {
  const [selection, setSelection] = useState<UserMap>(loadSelection);
  const selectUser = useCallback((org: OrgId, id: string) => {
    setSelection((prev) => {
      const next = { ...prev, [org]: id };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);
  const value = useMemo<UserSelectionValue>(() => ({ selection, selectUser }), [selection, selectUser]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function useUserSelection(): UserSelectionValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useUserSelection must be used within a UserProvider');
  return ctx;
}

/**
 * The active viewer for the current org, the org's people, and a `select`
 * action. `user` is undefined only while the directory is still loading or the
 * org has no people.
 */
export function useCurrentUser(): {
  user: Person | undefined;
  users: Person[];
  select: (id: string) => void;
} {
  const { orgId } = useOrg();
  const users = usePeople().data ?? [];
  const { selection, selectUser } = useUserSelection();
  const stored = selection[orgId];
  const user = users.find((p) => p.id === stored) ?? users.find((p) => p.you) ?? users[0];
  const select = useCallback((id: string) => selectUser(orgId, id), [orgId, selectUser]);
  return { user, users, select };
}

/** Just the active viewer's id (or '' while loading) — handy for filtering. */
export function useCurrentUserId(): string {
  return useCurrentUser().user?.id ?? '';
}
